import React, { useCallback, useMemo, useState } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import { useT } from '@transifex/react'
import type { MenuProps } from 'antd'
import { Dropdown } from 'src/antd'
import styled from 'styled-components'

import { gray200, gray300, blue100, navy900 } from 'src/styles/color-theme'

type AntdMenuItem = NonNullable<MenuProps['items']>[number]

type SearchableItem = {
  title: string
  path: string
  parentLabel?: string
  icon?: React.ReactNode
  onClick?: () => void
}

type SearchBoxProps = {
  menuItems: AntdMenuItem[]
  onMenuOpen: (parentKey: string) => void
  collapsed?: boolean
  onToggleSidebar?: () => void
}

const SearchWrapper = styled.div`
  padding: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.bSection || gray200};
  border-inline-end: 1px solid rgba(5, 5, 5, 0.06);
`

const SearchContainer = styled.div<{ $isInput: boolean }>`
  width: 100%;
  height: 32px;
  border-radius: 8px;
  background: ${({ theme }) => theme.bgInnerSecondary || '#f8f9fa'};
  border: 1px solid ${({ theme }) => theme.bSection || gray200};
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  cursor: ${({ $isInput }) => ($isInput ? 'text' : 'pointer')};
  padding: 0;
  margin: 0;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.bgInnerPrimary || '#ffffff'};
    border-color: ${({ theme }) => theme.gray500 || gray300};
  }

  &:focus-within {
    outline: none;
    border-color: ${({ theme }) => theme.primary || blue100};
    background: ${({ theme }) => theme.bgInnerPrimary || '#ffffff'};
  }
`

const SearchIcon = styled(SearchOutlined)`
  position: absolute;
  left: 11px;
  color: ${({ theme }) => theme.textSecondary || gray300};
  font-size: 14px;
  pointer-events: none;
  z-index: 1;
`

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0 11px 0 35px;
  font-size: 14px;
  color: ${({ theme }) => theme.textPrimary || navy900};
  font-family: inherit;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.textSecondary || gray300};
  }
`

const SearchText = styled.div`
  color: ${({ theme }) => theme.textSecondary || gray300};
  font-size: 14px;
  padding: 0 11px 0 35px;
  width: 100%;
  display: flex;
  align-items: center;
  height: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SearchDropdown = styled.div`
  background: ${({ theme }) => theme.bgInnerPrimary || 'white'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid ${({ theme }) => theme.bSection || gray200};
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
`

const SearchResultItem = styled.div<{ $isSelected: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid ${({ theme }) => theme.bSection || gray200};
  background-color: ${({ $isSelected }) =>
    $isSelected ? blue100 : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.bgInnerSecondary || blue100};
  }

  &:last-child {
    border-bottom: none;
  }

  .search-result-title {
    font-weight: 500;
    color: ${({ theme }) => theme.textPrimary || navy900};
  }

  .search-result-path {
    font-size: 12px;
    color: ${({ theme }) => theme.textSecondary || navy900};
    margin-top: 2px;
    opacity: 0.7;
  }
`

const SearchBox: React.FC<SearchBoxProps> = ({
  menuItems,
  onMenuOpen,
  collapsed,
  onToggleSidebar,
}) => {
  const t = useT()
  const [searchQuery, setSearchQuery] = useState('')
  const [isInputMode, setIsInputMode] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const searchableItems = useMemo<SearchableItem[]>(() => {
    const items: SearchableItem[] = []

    menuItems.forEach((menuItem) => {
      if (!menuItem) return

      const hasChildren =
        'children' in menuItem && Array.isArray(menuItem.children)

      if (hasChildren) {
        const parentItem = menuItem as {
          key: string
          label: string
          children: Array<{
            key: string
            label: string
            icon?: React.ReactNode
            onClick?: () => void
          }>
        }
        const { key: parentKey, label: parentLabel, children } = parentItem
        children?.forEach((child) => {
          if (child?.label && child?.key) {
            items.push({
              title: child.label,
              path: child.key,
              parentLabel,
              icon: child.icon,
              onClick: () => {
                child.onClick?.()
                onMenuOpen(parentKey)
              },
            })
          }
        })
      } else if ('onClick' in menuItem && 'label' in menuItem) {
        const directItem = menuItem as {
          key: string
          label: string
          icon: React.ReactNode
          onClick?: () => void
        }
        const { key, label, icon, onClick } = directItem
        if (onClick && label) {
          items.push({ title: label, path: key, icon, onClick })
        }
      }
    })

    return items
  }, [menuItems, onMenuOpen])

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.toLowerCase()
    return searchableItems.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.parentLabel?.toLowerCase().includes(query)
    )
  }, [searchQuery, searchableItems])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
      setSelectedIndex(-1)
    },
    []
  )

  const handleResultClick = useCallback((item: SearchableItem) => {
    item.onClick?.()
    setSearchQuery('')
    setIsInputMode(false)
    setSelectedIndex(-1)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!filteredResults.length) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev + 1) % filteredResults.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(
            (prev) =>
              (prev - 1 + filteredResults.length) % filteredResults.length
          )
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0)
            handleResultClick(filteredResults[selectedIndex])
          break
        case 'Escape':
          setSearchQuery('')
          setIsInputMode(false)
          setSelectedIndex(-1)
          break
        default:
          break
      }
    },
    [filteredResults, selectedIndex, handleResultClick]
  )

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!searchQuery) setIsInputMode(false)
    }, 200)
  }, [searchQuery])

  const handleClick = useCallback(() => {
    if (collapsed && onToggleSidebar) {
      onToggleSidebar()
    } else {
      setIsInputMode(true)
    }
  }, [collapsed, onToggleSidebar])

  const shouldShowDropdown =
    !collapsed && !!searchQuery && !!filteredResults.length

  return (
    <SearchWrapper>
      <Dropdown
        open={shouldShowDropdown}
        placement="bottomLeft"
        dropdownRender={() => (
          <SearchDropdown>
            {filteredResults.length > 0 ? (
              filteredResults.map((item, index) => (
                <SearchResultItem
                  key={`${item.path}-${index}`}
                  $isSelected={selectedIndex === index}
                  onClick={() => handleResultClick(item)}
                >
                  <div className="search-result-title">
                    {item.icon && (
                      <span style={{ marginRight: 8 }}>{item.icon}</span>
                    )}
                    {item.title}
                  </div>
                  {item.parentLabel && (
                    <div className="search-result-path">
                      {item.parentLabel} → {item.title}
                    </div>
                  )}
                </SearchResultItem>
              ))
            ) : (
              <div style={{ padding: 12, textAlign: 'center', color: gray300 }}>
                {t('No results found')}
              </div>
            )}
          </SearchDropdown>
        )}
      >
        <SearchContainer
          $isInput={isInputMode && !collapsed}
          as={isInputMode && !collapsed ? 'div' : 'button'}
          onClick={isInputMode && !collapsed ? undefined : handleClick}
        >
          <SearchIcon />
          {!collapsed &&
            (isInputMode ? (
              <SearchInput
                placeholder={t('Search menu...')}
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                autoFocus
              />
            ) : (
              <SearchText>{t('Search menu...')}</SearchText>
            ))}
        </SearchContainer>
      </Dropdown>
    </SearchWrapper>
  )
}

export default SearchBox
